<template>
  <b-form-group :label="field.label" :label-for="`${field.key}Input`" :description="field.description">
    <b-spinner v-if="loading" :style="{ height: '32px', width: '32px' }" type="grow" variant="primary"/>
    <div class="d-flex flex-row" v-else>
      <a class="mr-2" :href="value.fileLocation" target="_blank" v-if="showPreview && value && value.fileLocation">
        <b-img v-if="type === 'IMAGE'" :src="value.fileLocation | cdn('resize', 40, 40)" :alt="value.fileName || null"
          height="40px" width="40px"/>
      </a>
      <div class="d-flex flex-column flex-fill">
        <b-form-file :id="id || `${field.key}Input`" :placeholder="value && value.fileName ? value.fileName : placeholder"
          :accept="accept" :class="{ 'is-invalid': err }" :disabled="disabled" :state="state" @change="e => receivedFile(e)"/>
        <div v-if="err" class="invalid-feedback">
          <template v-if="err.message">{{ err.message }}</template>
          <template v-else>{{ `${err}` }}</template>
        </div>
        <a v-else-if="value && value.fileLocation" class="text-danger text-decoration-none" @click.prevent="() => $emit('input', null)" href="#">
          &times; {{ $t('ctas.clear') }}
        </a>
      </div>
    </div>
    <b-progress v-if="showProgress && percentage !== 0" :value="percentage" :max="100" show-progress animated/>
  </b-form-group>
</template>

<script>
import assetUploadMixin from '@/mixins/assetUpload';
import { createSha1File } from '@/lib/hashing';

export default {
  props: {
    field: Object,
    accept: String,
    id: String,
    disabled: Boolean,
    placeholder: String,
    showPreview: {
      type: Boolean,
      default: () => false,
    },
    showProgress: {
      type: Boolean,
      default: () => false,
    },
    state: {
      type: Boolean,
      default: () => null,
    },
    value: Object,
  },
  mixins: [ assetUploadMixin ],
  data: () => ({
    err: null,
    loading: false,
    percentage: 0
  }),
  computed: {
    type() {
      if (this.field && this.field.type && this.field.type.indexOf('asset-') === 0) {
        return this.field.type.substr('asset-'.length).toUpperCase();
      } else if (this.field && this.field.type && [ 'IMAGE', 'DOCUMENT', 'VIDEO' ].includes(this.field.type.toUpperCase())) {
        return this.field.type.toUpperCase();
      } else {
        return null;
      }
    },
  },
  methods: {
    async receivedFile(e) {
      // reset error
      this.err = null;

      try {
        this.$assert(e.target.files && e.target.files.length, new Error('Expected a file to be selected'));

        const [ file ] = e.target.files;
        this.$log.debug(file);
        this.loading = true;

        // check image size: <5mb
        this.$assert(!(this.type === 'IMAGE' && file.size > 5000000), 'Image too big, please upload a smaller one. No more than 5Mb.');

        // check document size: <100mb
        this.$assert(!(this.type === 'DOCUMENT' && file.size > 100000000), 'Document too big, please upload a smaller one. No more than 100Mb.');

        // check video size: >75kb, <500mb
        this.$assert(!(this.type === 'VIDEO' && file.size < 75000), 'Video too small, please upload a larger one. No less than 75Kb, or more than 500Mb.');
        this.$assert(!(this.type === 'VIDEO' && file.size > 500000000), 'Video too large, please upload a smaller one. No less than 75Kb, or more than 500Mb.');

        const { token, asset: uploading, upload } = await this.createAssetMutation({
          type: this.type,
          fileName: file.name,
          fileMime: file.type,
          fileSize: file.size,
          fileSha1: await createSha1File(file),
        });

        this.$log.debug({ file: file.name, uploading, upload });

        await this.initAssetUpload(upload, file, {
          onUploadProgress: e => {
            this.percentage = Math.round((e.loaded * 100) / e.total);
          }
        });

        const asset = await this.uploadedAssetMutation(token);
        this.$emit('input', asset);
      } catch (err) {
        this.$log.error(err);
        this.err = err;
      } finally {
        this.loading = false;
        this.percentage = 0;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/settings/AssetBlock.vue